import { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';

// Colour helpers reference the theme CSS variables so the card stays in sync
// with the design tokens even though it renders raw markup for the mock.
const C = {
  red: 'var(--red)', green: 'var(--green)', cyan: 'var(--cyan)',
  amber: 'var(--amber)', lime: 'var(--lime)', violet: 'var(--violet)', muted: 'var(--tx-muted)',
};

// Scouting reports cycle through the card. Data mirrors the game's fog-of-war
// tiers (Unscouted / Basic / Full) and distance-based uncertainty — the São Paulo
// report comes back with much wider ranges than the local ones.
const REPORTS = [
  { region: 'East London', tier: { t: 'Grassroots', c: 'b-violet' }, rows: [
    { pos: 'ST',  pc: C.red,    name: '??? Bennett',   fog: true,  meta: '17 · Unscouted',    ca: '??',    cc: '',      caFog: true },
    { pos: 'CB',  pc: C.green,  name: 'Reuben Okafor', fog: false, meta: '18 · Basic',        ca: '38–48', cc: C.amber, caFog: false },
    { pos: 'CM',  pc: C.cyan,   name: 'Dylan Reece',   fog: false, meta: '17 · Full',         ca: '44',    cc: C.lime,  caFog: false },
    { pos: 'MID', pc: C.muted,  name: '??? Osei',      fog: true,  meta: '16–18 · Unscouted', ca: '??',    cc: '',      caFog: true },
    { pos: 'GK',  pc: C.amber,  name: 'Finn Doherty',  fog: false, meta: '18 · Basic',        ca: '31–41', cc: C.amber, caFog: false },
  ] },
  { region: 'Merseyside', tier: { t: 'Youth', c: 'b-cyan' }, rows: [
    { pos: 'LW', pc: C.cyan,   name: 'Kai Whitfield', fog: false, meta: '16 · Full',         ca: '51',    cc: C.lime,  caFog: false },
    { pos: 'CB', pc: C.green,  name: '??? Marsh',     fog: true,  meta: '17 · Unscouted',    ca: '??',    cc: '',      caFog: true },
    { pos: 'ST', pc: C.red,    name: 'Tomas Varga',   fog: false, meta: '19 · Basic',        ca: '42–52', cc: C.amber, caFog: false },
    { pos: 'CM', pc: C.cyan,   name: 'Louis Adeyemi', fog: false, meta: '18 · Full',         ca: '48',    cc: C.lime,  caFog: false },
    { pos: 'RB', pc: C.amber,  name: '??? Nkemelu',   fog: true,  meta: '16–18 · Unscouted', ca: '??',    cc: '',      caFog: true },
  ] },
  { region: 'Greater Manchester', tier: { t: 'Grassroots', c: 'b-violet' }, rows: [
    { pos: 'CAM', pc: C.violet, name: 'Ryan Docherty', fog: false, meta: '17 · Full',         ca: '46',    cc: C.lime,  caFog: false },
    { pos: 'GK',  pc: C.amber,  name: '??? Pryce',     fog: true,  meta: '18 · Unscouted',    ca: '??',    cc: '',      caFog: true },
    { pos: 'CB',  pc: C.green,  name: 'Emeka Obi',     fog: false, meta: '19 · Basic',        ca: '40–50', cc: C.amber, caFog: false },
    { pos: 'LM',  pc: C.cyan,   name: 'Sol Bright',    fog: false, meta: '16 · Basic',        ca: '29–39', cc: C.amber, caFog: false },
    { pos: 'ST',  pc: C.red,    name: '??? Kavanagh',  fog: true,  meta: '17 · Unscouted',    ca: '??',    cc: '',      caFog: true },
  ] },
  { region: 'São Paulo', tier: { t: 'Distant · ±15', c: 'b-violet' }, rows: [
    { pos: 'ST', pc: C.red,    name: '??? Ribeiro',   fog: true,  meta: '18 · Unscouted',    ca: '??',    cc: '',      caFog: true },
    { pos: 'RW', pc: C.cyan,   name: 'Léo Ferraz',    fog: false, meta: '17 · Basic',        ca: '44–64', cc: C.amber, caFog: false },
    { pos: 'CM', pc: C.muted,  name: '??? Duarte',    fog: true,  meta: '16–19 · Unscouted', ca: '??',    cc: '',      caFog: true },
    { pos: 'CB', pc: C.green,  name: 'Mateus Pinto',  fog: false, meta: '19 · Basic',        ca: '38–58', cc: C.amber, caFog: false },
    { pos: 'SS', pc: C.violet, name: 'Gabriel Rocha', fog: false, meta: '18 · Full',         ca: '55',    cc: C.lime,  caFog: false },
  ] },
];

const SPEED = 40;       // ms per character while typing
const ZOOM = 2.0;       // loupe magnification
const DIAM = 150;       // loupe diameter in px
const HOLD_MS = 2200;   // pause once a report is fully typed
const ERASE_STEP = 2;   // characters removed per tick when clearing

export default function ScoutingCard() {
  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const stRef = useRef({ ri: 0, row: 0, ch: 0, phase: 'type', hold: 0 });
  const [, bump] = useReducer((x) => (x + 1) & 0xffff, 0);
  const pausedRef = useRef(false);
  const posRef = useRef({ x: 0, y: 0 });

  const cardRef = useRef(null);
  const loupeRef = useRef(null);
  const contentRef = useRef(null);
  const handleRef = useRef(null);
  const [inspecting, setInspecting] = useState(false);

  // Typewriter loop. Paused while the loupe is open.
  useEffect(() => {
    if (prefersReduced) {
      stRef.current = { ri: 0, row: REPORTS[0].rows.length, ch: 0, phase: 'hold', hold: 0 };
      bump();
      return undefined;
    }
    const id = setInterval(() => {
      if (pausedRef.current) return;
      const st = stRef.current;
      const rows = REPORTS[st.ri].rows;
      if (st.phase === 'type') {
        const cur = rows[st.row];
        if (st.ch < cur.name.length) st.ch += 1;
        else {
          st.row += 1; st.ch = 0;
          if (st.row >= rows.length) { st.phase = 'hold'; st.hold = Math.round(HOLD_MS / SPEED); }
        }
      } else if (st.phase === 'hold') {
        st.hold -= 1;
        if (st.hold <= 0) { st.phase = 'erase'; st.row = rows.length - 1; st.ch = rows[st.row].name.length; }
      } else if (st.phase === 'erase') {
        if (st.ch > 0) st.ch = Math.max(0, st.ch - ERASE_STEP);
        else {
          st.row -= 1;
          if (st.row < 0) { st.ri = (st.ri + 1) % REPORTS.length; st.row = 0; st.ch = 0; st.phase = 'type'; }
          else st.ch = rows[st.row].name.length;
        }
      }
      bump();
    }, SPEED);
    return () => clearInterval(id);
  }, [prefersReduced]);

  const positionLoupe = useCallback(() => {
    const card = cardRef.current, loupe = loupeRef.current, content = contentRef.current, handle = handleRef.current;
    if (!card || !loupe || !content) return;
    const rect = card.getBoundingClientRect();
    const R = DIAM / 2;
    const { x, y } = posRef.current;
    const rx = x - rect.left, ry = y - rect.top;
    content.style.width = `${rect.width}px`;
    loupe.style.left = `${x - R}px`;
    loupe.style.top = `${y - R}px`;
    content.style.transform = `translate(${R - rx * ZOOM}px, ${R - ry * ZOOM}px) scale(${ZOOM})`;
    if (handle) {
      const a = Math.PI / 4;
      handle.style.left = `${x + Math.cos(a) * (R - 1)}px`;
      handle.style.top = `${y + Math.sin(a) * (R - 1)}px`;
      handle.style.transform = 'rotate(45deg)';
    }
  }, []);

  useLayoutEffect(() => {
    if (inspecting) positionLoupe();
  }, [inspecting, positionLoupe]);

  function handleEnter(e) {
    posRef.current = { x: e.clientX, y: e.clientY };
    pausedRef.current = true;
    setInspecting(true);
  }
  function handleMove(e) {
    posRef.current = { x: e.clientX, y: e.clientY };
    if (pausedRef.current) positionLoupe();
  }
  function handleLeave() {
    pausedRef.current = false;
    setInspecting(false);
  }

  const st = stRef.current;
  const rep = REPORTS[st.ri];

  const rows = rep.rows.map((r, i) => {
    let pos = '', name = null, meta = '', ca = '';
    const showAll = i < st.row || st.phase === 'hold';
    if (showAll) { pos = r.pos; name = r.name; meta = r.meta; ca = r.ca; }
    else if (i === st.row) {
      pos = r.pos;
      name = (<>{r.name.slice(0, st.ch)}<span className="sc-caret" /></>);
    }
    const nameCls = 'hp-name' + (r.fog && (i <= st.row || st.phase === 'hold') ? ' hp-fog' : '');
    const caCls = 'hp-ca' + (r.caFog ? ' hp-fog' : '');
    const caStyle = r.caFog || !r.cc ? undefined : { color: r.cc };
    return (
      <div className="hp-row" key={i}>
        <span className="hp-pos" style={{ color: r.pc }}>{pos}</span>
        <span className={nameCls}>{name}</span>
        <span className="hp-meta">{meta}</span>
        <span className={caCls} style={caStyle}>{ca}</span>
      </div>
    );
  });

  const inner = (
    <>
      <div className="hp-head">
        <span className="hp-title">Scouting · {rep.region}</span>
        <span className={`badge ${rep.tier.c}`} style={{ marginLeft: 'auto' }}>{rep.tier.t}</span>
      </div>
      <div className="hp-body">{rows}</div>
    </>
  );

  return (
    <>
      <div
        className={`hero-panel${inspecting ? ' inspecting' : ''}`}
        ref={cardRef}
        onMouseEnter={handleEnter}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        aria-hidden="true"
      >
        {inner}
      </div>
      {inspecting && (
        <div className="sc-loupe" ref={loupeRef} style={{ width: DIAM, height: DIAM }}>
          <div className="sc-loupe-content" ref={contentRef}>
            <div className="hero-panel" style={{ boxShadow: 'none' }}>{inner}</div>
          </div>
        </div>
      )}
      {inspecting && <div className="sc-handle" ref={handleRef} />}
    </>
  );
}
